// src/utils/liveResults.ts
// ─────────────────────────────────────────────────────────────────────────────
//  Zápisy a čtení živých výsledků (fáze 1 živého bodování,
//  docs/live-faze1-navrh.md).
//
//  Každý zápis mění jen JEDNO stanoviště střelce – zápis odeslaný později
//  z telefonu, který byl bez signálu, tak nepřepíše výsledky ostatních
//  stanovišť, rozstřel ani opravy. Zápis a smazání stanoviště rozhodčím jdou
//  přes setDoc s mergeFields nebo updateDoc s FieldPath; oprava hlavního
//  rozhodčího a správce (correctionWrite) přes setDoc s { merge: true } –
//  ne mergeFields, zámky dřívějších oprav (lockedStations) by zmizely (J140).
//  Pravidla (firestore.rules) totéž vynucují: v jednom zápisu nejvýš jedno
//  stanoviště; rozhodčí jen to, které jmenuje v `lastStationId`, jako sám sebe
//  (`enteredBy`) a ne stanoviště opravené hlavním rozhodčím (`lockedStations`).
//
//  Součet bodů se počítá při čtení (`liveTotal`) jen z nastavených stanovišť
//  (id jako u čtenářů – uložené id má přednost); uložený `totalScore` je jen
//  orientační kopie pro starší otevřené klienty.
// ─────────────────────────────────────────────────────────────────────────────

import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  FieldPath,
  getDocsFromServer,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type SetOptions,
  type WriteBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import type { LiveResult, LiveRound, LiveStation } from "../types/liveScoring";
import { firebaseErrorMessage } from "./firebaseErrors";
import { roundPoints } from "./formatPoints";
import { reportWarning } from "./reportError";

// ── Cesty ───────────────────────────────────────────────────────────────────

/** Dokument výsledků střelce: races/{raceId}/live_results/{startovní číslo}. */
export const liveResultRef = (raceId: string, startNumber: number) =>
  doc(db, `races/${raceId}/live_results`, String(startNumber));

/** Koncept střel rozhodčího: races/{raceId}/live_drafts/{stanoviště}_{startovní číslo}. */
export const liveDraftRef = (raceId: string, stationId: string, startNumber: number) =>
  doc(db, `races/${raceId}/live_drafts`, `${stationId}_${startNumber}`);

// ── Součet bodů ─────────────────────────────────────────────────────────────

type StationsLike =
  | { [stationId: string]: { score?: unknown } | null | undefined }
  | null
  | undefined;

/**
 * Id nastavených stanovišť závodu – tak, jak je skládají čtenáři
 * (`{ id: d.id, ...d.data() }`: uložené id má přednost před id dokumentu,
 * např. OM Slovenska open: dokument `st5` s uloženým id `st6`).
 */
export type LiveStationIds = ReadonlyArray<string> | ReadonlySet<string>;

const stationIdSet = (ids: LiveStationIds): ReadonlySet<string> =>
  Array.isArray(ids) ? new Set<string>(ids) : (ids as ReadonlySet<string>);

/** Body stanoviště: konečné číslo, jinak null (řetězec ani NaN součet nerozbije). */
function stationScoreOf(entry: unknown): number | null {
  const score = entry && typeof entry === "object" ? (entry as { score?: unknown }).score : undefined;
  return typeof score === "number" && Number.isFinite(score) ? score : null;
}

/**
 * Součet bodů ze stanovišť (2 des. místa bez float šumu); nečíselné hodnoty
 * přeskočí. S `stationIds` sečte jen nastavená stanoviště – klíč, který mezi
 * nimi není (podvržené `st01`, výsledek pod starým id po přečíslování), se
 * nezapočítá (návrh fáze 1, bod 3.4). Bez nich – nebo s prázdným seznamem
 * (stanoviště se nenačetla: tabule má ukázat součty, ne nuly; publikace
 * s prázdným seznamem stanovišť skončí chybou dřív) – sečte všechny klíče
 * mapy jako dřívější zapisovatelé `totalScore`. V produkci
 * jsou všechny klíče výsledků nastavená stanoviště (ověřeno 5. 10. 2026, i
 * posunuté `st6` OM Slovenska open) – součet vyjde u obou variant stejně.
 */
export function liveTotalOfStations(stations: StationsLike, stationIds?: LiveStationIds | null): number {
  if (!stations || typeof stations !== "object") return 0;
  const set = stationIds ? stationIdSet(stationIds) : null;
  const only = set && set.size > 0 ? set : null;
  let sum = 0;
  for (const [stationId, entry] of Object.entries(stations)) {
    if (only && !only.has(stationId)) continue;
    const score = stationScoreOf(entry);
    if (score !== null) sum += score;
  }
  return roundPoints(sum, 2);
}

/**
 * Součet bodů střelce – jediný zdroj pravdy pro tabuli, Živé výsledky
 * i publikaci; ty předávají id nastavených stanovišť (`stationIds`), takže
 * body pod klíčem mimo stanoviště nepočítá nikdo z nich.
 */
export function liveTotal(
  result: { stations?: StationsLike } | null | undefined,
  stationIds?: LiveStationIds | null
): number {
  return liveTotalOfStations(result ? result.stations : undefined, stationIds);
}

/**
 * Střelec má výsledek (fáze 4a, C1 a F1): aspoň jedno NASTAVENÉ stanoviště
 * s číselnými body – i zapsaná nula. Prázdný dokument, dokument jen
 * s rozstřelem nebo jen s body vyřazeného / smazaného stanoviště výsledek
 * není (nemá místo, medaili ani se nezveřejní). Bez stanovišť (žádná
 * nastavená, nenačtená) jako liveTotal: kterékoli stanoviště.
 */
export function hasLiveResult(
  result: { stations?: StationsLike } | null | undefined,
  stationIds?: LiveStationIds | null
): boolean {
  const stations = result ? result.stations : undefined;
  if (!stations || typeof stations !== "object") return false;
  const set = stationIds ? stationIdSet(stationIds) : null;
  const only = set && set.size > 0 ? set : null;
  return Object.entries(stations).some(
    ([stationId, entry]) => (!only || only.has(stationId)) && stationScoreOf(entry) !== null
  );
}

/**
 * Body pod klíči, které mezi nastavenými stanovišti nejsou – `liveTotal`
 * se `stationIds` je nezapočítá a žádný sloupec je neukazuje; Živé výsledky
 * na ně upozorní (ověřit nastavení stanovišť). Jen konečná čísla (ostatní se
 * nepočítají nikdy).
 */
export function unconfiguredStationScores(
  result: { stations?: StationsLike } | null | undefined,
  stationIds: LiveStationIds
): Array<{ stationId: string; score: number }> {
  const stations = result ? result.stations : undefined;
  if (!stations || typeof stations !== "object") return [];
  const configured = stationIdSet(stationIds);
  const out: Array<{ stationId: string; score: number }> = [];
  for (const [stationId, entry] of Object.entries(stations)) {
    if (configured.has(stationId)) continue;
    const score = stationScoreOf(entry);
    if (score !== null) out.push({ stationId, score });
  }
  return out;
}

// ── Zápis výsledku stanoviště (rozhodčí, hlavní rozhodčí na obrazovce rozhodčího) ──

/** Hlavička střelce – zakládá dokument při prvním stanovišti. */
export interface LiveShooterHeader {
  startNumber: number;
  userId?: string | null;
  sequenceNumber?: string | null;
  name?: string | null;
  roundNumber: number;
}

/** Výsledek stanoviště, jak ho zadává rozhodčí (čas a serverové razítko doplní helper). */
export interface StationEntryInput {
  score: number;
  shots: number[];
  gongValues?: number[];
  /** uid rozhodčího – pravidla vyžadují, aby zapisoval sám za sebe */
  enteredBy: string;
}

const HEADER_FIELDS = ["startNumber", "userId", "sequenceNumber", "name", "roundNumber"] as const;

function headerData(header: LiveShooterHeader) {
  return {
    startNumber: header.startNumber,
    userId: header.userId || "",
    sequenceNumber: header.sequenceNumber || "",
    name: header.name || "",
    roundNumber: header.roundNumber,
  };
}

/**
 * Data a `mergeFields` pro setDoc / batch.set výsledku jednoho stanoviště.
 * Zapíše hlavičku (založí dokument), `stations.<id>` celé, `lastStationId`,
 * orientační `totalScore`, `updatedAt` a odebere rozstřel (jiný součet = rozstřel
 * neplatí – stejně jako dřívější zápis celého dokumentu). Ostatní stanoviště,
 * `lockedStations` a cizí pole zůstanou netknutá.
 *
 * `existing` = místní kopie dokumentu (jen pro orientační součet).
 */
export function stationResultWrite(
  header: LiveShooterHeader,
  stationId: string,
  entry: StationEntryInput,
  existing?: Pick<LiveResult, "stations"> | null
): { data: Record<string, unknown>; options: SetOptions } {
  const stored: Record<string, unknown> = {
    score: entry.score,
    shots: Array.isArray(entry.shots) ? entry.shots : [],
    ...(Array.isArray(entry.gongValues) ? { gongValues: entry.gongValues } : {}),
    completedAt: serverTimestamp(),
    enteredBy: entry.enteredBy,
  };
  const localStations = { ...((existing && existing.stations) || {}), [stationId]: { score: entry.score } };
  const data: Record<string, unknown> = {
    ...headerData(header),
    totalScore: liveTotalOfStations(localStations),
    updatedAt: serverTimestamp(),
    lastStationId: stationId,
    tiebreaker: deleteField(),
    stations: { [stationId]: stored },
  };
  return {
    data,
    options: {
      mergeFields: [
        ...HEADER_FIELDS,
        "totalScore",
        "updatedAt",
        "lastStationId",
        "tiebreaker",
        new FieldPath("stations", stationId),
      ],
    },
  };
}

/** Přidá zápis výsledku stanoviště do dávky (JudgeView: výsledek + koncept + průběh). */
export function addStationResultToBatch(
  batch: WriteBatch,
  raceId: string,
  header: LiveShooterHeader,
  stationId: string,
  entry: StationEntryInput,
  existing?: Pick<LiveResult, "stations"> | null
): void {
  const { data, options } = stationResultWrite(header, stationId, entry, existing);
  batch.set(liveResultRef(raceId, header.startNumber), data, options);
}

/**
 * Uloží výsledek stanoviště v jedné dávce se smazáním konceptu střel (přijatý
 * výsledek koncept odstraní i po zavření stránky, zamítnutý ho nechá).
 * `extra` přidá do téže dávky další zápisy (průběh automatické rotace).
 * Promise se offline nevyřeší, dokud server zápis nepotvrdí.
 */
export async function saveStationResult(
  raceId: string,
  header: LiveShooterHeader,
  stationId: string,
  entry: StationEntryInput,
  existing?: Pick<LiveResult, "stations"> | null,
  opts: { deleteDraft?: boolean; extra?: (batch: WriteBatch) => void } = {}
): Promise<void> {
  const batch = writeBatch(db);
  addStationResultToBatch(batch, raceId, header, stationId, entry, existing);
  if (opts.deleteDraft !== false) batch.delete(liveDraftRef(raceId, stationId, header.startNumber));
  if (opts.extra) opts.extra(batch);
  await batch.commit();
}

/** Součet bez daného stanoviště (orientační kopie po smazání). */
function totalWithout(existing: Pick<LiveResult, "stations"> | null | undefined, stationId: string): number {
  const rest = { ...((existing && existing.stations) || {}) };
  delete rest[stationId];
  return liveTotalOfStations(rest);
}

/**
 * Smazání výsledku stanoviště rozhodčím (dual „Opravit #N“): jen `stations.<id>`,
 * s `lastStationId` (pravidla) a odebráním rozstřelu. Dokument musí existovat.
 */
export function removeStationResultAsJudge(
  raceId: string,
  startNumber: number,
  stationId: string,
  existing?: Pick<LiveResult, "stations"> | null
): Promise<void> {
  return updateDoc(
    liveResultRef(raceId, startNumber),
    new FieldPath("stations", stationId),
    deleteField(),
    "lastStationId",
    stationId,
    "tiebreaker",
    deleteField(),
    "totalScore",
    totalWithout(existing, stationId),
    "updatedAt",
    serverTimestamp()
  );
}

// ── Opravy hlavního rozhodčího a správce (Živé výsledky) ──────────────────────

/** Rozstřel platí jen pro původní součet: změní-li se součet, odebere se. */
function totalChanged(before: number, after: number): boolean {
  return Math.abs(before - after) > 0.0005;
}

/**
 * Data pro opravu (nebo ruční zadání) výsledku stanoviště hlavním rozhodčím
 * či správcem – setDoc s `{ merge: true }`. Stanoviště se zamkne
 * (`lockedStations: arrayUnion`) – pozdní zápis rozhodčího ho už nepřepíše.
 * Rozstřel se odebere, když se změní součet. `entry` = jen měněné listy
 * stanoviště ({ score, editedBy, editedAt }): ostatní pole (shots,
 * gongValues, completedAt, enteredBy) `merge: true` na serveru nechá – místní
 * kopie může být zastaralá (rozhodčí stanoviště mezitím znovu zadal,
 * neodeslaný zápis z jiné záložky má completedAt null) a vracela by je zpět.
 * `existing` (místní kopie) slouží jen k součtu a rozhodnutí o rozstřelu.
 *
 * Proč `merge: true` a ne `mergeFields`: cesta v `mergeFields`, kterou plní jen
 * arrayUnion, se na serveru nejdřív smaže a pak sjednotí – zámky dřívějších
 * oprav téhož střelce by zmizely (ověřeno v emulátoru, test-a.js facts).
 * `merge: true` zapíše jen listy dat (stations.<id>.score …) a ostatní
 * stanoviště, zámky i rozstřel nechá; funguje i bez existujícího dokumentu.
 */
export function correctionWrite(
  header: LiveShooterHeader,
  stationId: string,
  entry: Record<string, unknown>,
  existing?: (Pick<LiveResult, "stations"> & { tiebreaker?: number }) | null
): { data: Record<string, unknown>; options: SetOptions; total: number; tiebreakerCleared: boolean } {
  const before = liveTotal(existing || undefined);
  const localStations = { ...((existing && existing.stations) || {}), [stationId]: entry as { score?: unknown } };
  const total = liveTotalOfStations(localStations);
  const tiebreakerCleared = !!(existing && existing.tiebreaker) && totalChanged(before, total);
  const data: Record<string, unknown> = {
    ...headerData(header),
    totalScore: total,
    updatedAt: serverTimestamp(),
    lockedStations: arrayUnion(stationId),
    stations: { [stationId]: entry },
  };
  if (tiebreakerCleared) data.tiebreaker = deleteField();
  return { data, options: { merge: true }, total, tiebreakerCleared };
}

/**
 * Smazání výsledku stanoviště hlavním rozhodčím / správcem, aby ho rozhodčí
 * zadal znovu: smaže `stations.<id>`, odemkne ho (`lockedStations`) a odebere
 * rozstřel, pokud se změní součet. Dokument musí existovat.
 */
export async function deleteStationResultAsHeadJudge(
  raceId: string,
  startNumber: number,
  stationId: string,
  existing: (Pick<LiveResult, "stations"> & { tiebreaker?: number }) | null | undefined
): Promise<{ total: number; tiebreakerCleared: boolean }> {
  const before = liveTotal(existing || undefined);
  const total = totalWithout(existing, stationId);
  const tiebreakerCleared = !!(existing && existing.tiebreaker) && totalChanged(before, total);
  const rest: unknown[] = [
    "lockedStations",
    arrayRemove(stationId),
    "totalScore",
    total,
    "updatedAt",
    serverTimestamp(),
  ];
  if (tiebreakerCleared) rest.push("tiebreaker", deleteField());
  await (updateDoc as (...args: unknown[]) => Promise<void>)(
    liveResultRef(raceId, startNumber),
    new FieldPath("stations", stationId),
    deleteField(),
    ...rest
  );
  return { total, tiebreakerCleared };
}

// ── Rozstřel ────────────────────────────────────────────────────────────────

/** Rozstřel skupiny (vyšší = lepší; null = odebrat) v jedné dávce – jen {tiebreaker, updatedAt}. */
export async function setTiebreakers(
  raceId: string,
  entries: Array<{ startNumber: number; tiebreaker: number | null }>
): Promise<void> {
  if (entries.length === 0) return;
  const batch = writeBatch(db);
  for (const e of entries) {
    batch.update(liveResultRef(raceId, e.startNumber), {
      tiebreaker: e.tiebreaker === null ? deleteField() : e.tiebreaker,
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

// ── Seznam rozhodčích závodu (races/{id}.liveJudgeUids) ──────────────────────

function uidList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && v !== "") : [];
}

/** Rozhodčí stanovišť, kteří chybí v seznamu rozhodčích závodu. */
export function missingLiveJudges(
  race: { liveJudgeUids?: unknown } | null | undefined,
  stations: Array<{ judgeUids?: unknown } | null | undefined>
): string[] {
  const listed = new Set(uidList(race ? race.liveJudgeUids : undefined));
  const missing = new Set<string>();
  for (const st of stations) {
    for (const uid of uidList(st ? st.judgeUids : undefined)) {
      if (!listed.has(uid)) missing.add(uid);
    }
  }
  return Array.from(missing);
}

/**
 * Samooprava: doplní do races/{id}.liveJudgeUids rozhodčí stanovišť, kteří
 * chybí (závody nastavené před fází 1 nebo starou verzí aplikace). Jen přidává.
 * Volá ji správce nebo hlavní rozhodčí; chyba (např. offline, stará pravidla)
 * se jen zaznamená. Vrací přidané uid.
 */
export async function ensureLiveJudges(
  raceId: string,
  race: { liveJudgeUids?: unknown } | null | undefined,
  stations: Array<{ judgeUids?: unknown } | null | undefined>
): Promise<string[]> {
  const missing = missingLiveJudges(race, stations);
  if (missing.length === 0) return [];
  try {
    await updateDoc(doc(db, "races", raceId), { liveJudgeUids: arrayUnion(...missing) });
    return missing;
  } catch (err) {
    reportWarning("liveResults.ensureLiveJudges", err, { raceId, missing: missing.length });
    return [];
  }
}

// ── Čtení ze serveru (publikace) ─────────────────────────────────────────────

/**
 * Živé výsledky závodu ze SERVERU (ne z místní mezipaměti, která po zápisech
 * jednoho stanoviště offline může držet neúplný dokument), podle startNumber.
 * Klíč stejně jako posluchače Živých výsledků a tabule (map[r.startNumber]):
 * číslo i číselný text ("101" – dokument dřívější verze), jinak by publikace
 * střelce, kterého tabulka ukazuje, zveřejnila s 0 body. Dokument bez čísla
 * se vynechá. Offline skončí chybou – publikovat se má jen z potvrzeného stavu.
 */
export async function loadLiveResultsFromServer(raceId: string): Promise<{ [sn: number]: LiveResult }> {
  const snap = await getDocsFromServer(collection(db, `races/${raceId}/live_results`));
  const map: { [sn: number]: LiveResult } = {};
  snap.docs.forEach((d) => {
    const r = d.data() as LiveResult;
    if (!r) return;
    const raw: unknown = r.startNumber;
    // Number(null) / Number("") by daly 0 – bez čísla se dokument vynechá
    const sn = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : NaN;
    if (Number.isFinite(sn)) map[sn] = r;
  });
  return map;
}

/**
 * Stanoviště závodu ze SERVERU, s id jako u čtenářů (`{ id: d.id, ...d.data() }`
 * – uložené id má přednost). Publikace z nich počítá součty a dílčí body:
 * seznam načtený při otevření obrazovky může být zastaralý (stanoviště přidané
 * za běhu by se do součtu nepočítalo). Offline skončí chybou.
 */
export async function loadLiveStationsFromServer(raceId: string): Promise<LiveStation[]> {
  const snap = await getDocsFromServer(collection(db, `races/${raceId}/live_stations`));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<LiveStation, "id">) }) as LiveStation)
    .sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
}

/**
 * Rundy závodu ZE SERVERU (návrh fáze 2, bod 4.5): { id dokumentu, number,
 * shooters } jako je čte obrazovka rozhodčího a Živé výsledky, seřazené podle
 * čísla. Publikace z nich bere, kdo na kterém čísle střílí – rundy načtené při
 * otevření obrazovky mohou být zastaralé (změna osoby na čísle, „nenastoupil“
 * za běhu by se do výsledků nedostaly). Pole střelců jiného typu = prázdná
 * runda. Offline skončí chybou.
 */
export async function loadLiveRoundsFromServer(raceId: string): Promise<LiveRound[]> {
  const snap = await getDocsFromServer(collection(db, `races/${raceId}/live_rounds`));
  return snap.docs
    .map((d) => {
      const data = (d.data() || {}) as Partial<LiveRound>;
      return {
        id: d.id,
        number: data.number as number,
        shooters: Array.isArray(data.shooters) ? data.shooters : [],
      };
    })
    .sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
}

// ── Hlášky ──────────────────────────────────────────────────────────────────

export const LIVE_SAVE_DENIED_MESSAGE =
  "Výsledek se neuložil – zápis není povolený (závod je ukončený, výsledek opravil hlavní rozhodčí, " +
  "nebo nejste v seznamu rozhodčích). Nahlaste ho hlavnímu rozhodčímu.";

/** Zápis zamítnut, protože stanoviště mezitím v nastavení smazali (fáze 3, F1). */
export const LIVE_SAVE_STATION_DELETED_MESSAGE =
  "Stanoviště bylo mezitím smazáno v nastavení – výsledek se neuložil. Nahlaste ho hlavnímu rozhodčímu.";

/**
 * Česká hláška pro neuložený výsledek rozhodčího. `stationDeleted` = server
 * potvrdil, že stanoviště už neexistuje (pravidla zápis na smazané
 * stanoviště zamítnou) – srozumitelnější než obecné „není povolený“.
 */
export function liveSaveErrorMessage(err: unknown, context: { stationDeleted?: boolean } = {}): string {
  const code = err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
  if (code === "permission-denied") return context.stationDeleted ? LIVE_SAVE_STATION_DELETED_MESSAGE : LIVE_SAVE_DENIED_MESSAGE;
  return `Výsledek se neuložil: ${firebaseErrorMessage(err)}`;
}
